import type { ReactNode } from 'react';
import clsx from 'clsx';
import { Ellipsis, EyeOff, Info, Maximize2, Minimize2 } from '@/components/ui/icons';
import { Popover } from '@/components/ui/Popover';
import { Menu, MenuItem } from '@/components/ui/Menu';
import { Tooltip } from '@/components/ui/Tooltip';
import { HOME_CARDS, setCardVisible, toggleCardExpanded, useHomeCards, type CardId } from './prefs';

export function HomeCard({
  id,
  actions,
  info,
  children,
  bodyClassName,
}: {
  id: CardId;
  actions?: ReactNode;
  info?: string;
  children: ReactNode;
  bodyClassName?: string;
}) {
  const meta = HOME_CARDS.find((c) => c.id === id)!;
  const expanded = useHomeCards((s) => s.expanded.includes(id));
  return (
    <section className={clsx('hm-card', `hm-card-${meta.size}`, { expanded })} aria-label={meta.title}>
      <header className="hm-card-head">
        <span className="hm-card-title">{meta.title}</span>
        {info && (
          <Tooltip label={info}>
            <span className="hm-card-info">
              <Info size={12} />
            </span>
          </Tooltip>
        )}
        <span className="spacer" />
        <span className="hm-card-actions">
          {actions}
          <Tooltip label={expanded ? 'Collapse' : 'Expand'}>
            <button className="icon-btn sm" onClick={() => toggleCardExpanded(id)} aria-label={expanded ? 'Collapse card' : 'Expand card'}>
              {expanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
          </Tooltip>
          <Popover
            placement="bottom-end"
            content={(close) => (
              <Menu style={{ width: 200 }}>
                <MenuItem icon={expanded ? <Minimize2 size={15} /> : <Maximize2 size={15} />} label={expanded ? 'Collapse card' : 'Expand card'} onClick={() => (close(), toggleCardExpanded(id))} />
                <MenuItem icon={<EyeOff size={15} />} label="Hide card" onClick={() => (close(), setCardVisible(id, false))} />
              </Menu>
            )}
          >
            <button className="icon-btn sm" aria-label="Card options">
              <Ellipsis size={14} />
            </button>
          </Popover>
        </span>
      </header>
      <div className={clsx('hm-card-body', bodyClassName)}>{children}</div>
    </section>
  );
}

export function CardEmpty({ icon, text, children }: { icon: ReactNode; text: ReactNode; children?: ReactNode }) {
  return (
    <div className="hm-empty">
      <div className="hm-empty-icon">{icon}</div>
      <div className="hm-empty-text">{text}</div>
      {children}
    </div>
  );
}
